<script setup>
import { ref, computed } from 'vue';
import { useApiData } from '../../composables/useApiData';
import { usePortalAccount } from '../../composables/usePortalAccount';
import axios from 'axios';
import Auth from 'dashboard/api/auth';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  widgetStyle: {
    type: Object,
    default: () => ({}),
  },
});

const { accountId } = usePortalAccount();

const title = computed(() => props.widget.title);
const searchQuery = ref('');
const currentPath = ref('/');

// État pour la modal de visualisation
const showFileModal = ref(false);
const selectedFile = ref(null);
const fileContent = ref(null);
const loadingFile = ref(false);
const fileError = ref(null);

// Récupérer le dossier racine configuré dans le widget
const rootFolder = computed(() => props.widget.props?.rootFolder || '/');

// S'assurer que le widget a un connecteur API configuré avec le bon endpoint
// On ne passe PAS folder_path à l'API car on veut récupérer tous les fichiers
// et filtrer côté client pour inclure le dossier racine et ses sous-dossiers
const widgetWithConnector = computed(() => {
  const widgetCopy = JSON.parse(JSON.stringify(props.widget));

  // Si le widget n'a pas de connecteur API, en créer un pour drive_files
  if (!widgetCopy.props?.apiConnector) {
    widgetCopy.props = widgetCopy.props || {};
    widgetCopy.props.apiConnector = {
      endpointId: 'drive_files',
      params: {
        // Ne pas passer folder_path pour récupérer tous les fichiers
        sort_by: 'updated_at',
        sort_order: 'desc',
      },
    };
  } else {
    // S'assurer que folder_path n'est pas dans les params pour récupérer tous les fichiers
    const params = { ...widgetCopy.props.apiConnector.params };
    delete params.folder_path;
    widgetCopy.props.apiConnector = {
      ...widgetCopy.props.apiConnector,
      params: {
        ...params,
        sort_by: params.sort_by || 'updated_at',
        sort_order: params.sort_order || 'desc',
      },
    };
  }

  return widgetCopy;
});

const { data, loading, error } = useApiData(widgetWithConnector, accountId);

// Debug logs pour diagnostiquer le problème
console.log('DriveWidget Debug:');
console.log('- Widget props:', props.widget);
console.log('- Root folder:', rootFolder.value);
console.log('- Account ID:', accountId.value);
console.log('- Widget connector:', widgetWithConnector.value);
console.log('- API Data:', data.value);
console.log('- Loading:', loading.value);
console.log('- Error:', error.value);

const items = computed(() => {
  let files = [];

  // Debug: vérifier la structure des données
  console.log('DriveWidget - Raw data structure:', {
    hasData: !!data.value,
    hasMapped: !!data.value?.mapped,
    hasRaw: !!data.value?.raw,
    mappedItems: data.value?.mapped?.items,
    rawItems: data.value?.raw?.items,
    rawArray: Array.isArray(data.value?.raw) ? data.value.raw : null,
  });

  if (data.value?.mapped?.items && Array.isArray(data.value.mapped.items)) {
    files = data.value.mapped.items;
    console.log('DriveWidget - Using mapped.items:', files.length);
  } else if (data.value?.raw?.items && Array.isArray(data.value.raw.items)) {
    files = data.value.raw.items;
    console.log('DriveWidget - Using raw.items:', files.length);
  } else if (data.value?.raw && Array.isArray(data.value.raw)) {
    files = data.value.raw;
    console.log('DriveWidget - Using raw array:', files.length);
  } else {
    console.log('DriveWidget - No files found in data structure');
  }

  // Exclure les fichiers .folder (placeholders)
  const originalLength = files.length;
  files = files.filter(file => file.name !== '.folder');
  console.log(
    `DriveWidget - Filtered .folder files: ${originalLength} -> ${files.length}`
  );

  // Filtrer les fichiers pour n'inclure que ceux du dossier racine et ses sous-dossiers
  if (rootFolder.value !== '/') {
    const filtered = files.filter(file => {
      const filePath = file.folder_path || '/';
      const matches =
        filePath === rootFolder.value ||
        filePath.startsWith(`${rootFolder.value}/`);
      return matches;
    });

    console.log('DriveWidget - Folder filtering:');
    console.log('- Root folder:', rootFolder.value);
    console.log('- Total files:', files.length);
    console.log('- Filtered files:', filtered.length);
    console.log(
      '- Sample files:',
      files.slice(0, 5).map(f => ({
        name: f.name,
        folder_path: f.folder_path,
        matches:
          f.folder_path === rootFolder.value ||
          f.folder_path?.startsWith(`${rootFolder.value}/`),
      }))
    );

    return filtered;
  }

  // Si rootFolder est '/', retourner tous les fichiers
  console.log('DriveWidget - Using all files (root folder):', files.length);
  return files;
});

const filteredItems = computed(() => {
  if (!searchQuery.value) return items.value;
  const query = searchQuery.value.toLowerCase();
  return items.value.filter(item => item.name.toLowerCase().includes(query));
});

const breadcrumbs = computed(() => {
  const parts = currentPath.value.split('/').filter(Boolean);
  const crumbs = [{ name: 'Root', path: '/' }];
  let path = '';
  parts.forEach(part => {
    path += `/${part}`;
    crumbs.push({ name: part, path });
  });
  return crumbs;
});

const navigateToFolder = path => {
  currentPath.value = path;
};

const getFileIcon = file => {
  const ext = file.name.split('.').pop().toLowerCase();
  const icons = {
    pdf: 'i-lucide-file-text',
    doc: 'i-lucide-file-text',
    docx: 'i-lucide-file-text',
    xls: 'i-lucide-table',
    xlsx: 'i-lucide-table',
    jpg: 'i-lucide-image',
    png: 'i-lucide-image',
    zip: 'i-lucide-archive',
  };
  return icons[ext] || 'i-lucide-file';
};

const formatFileSize = size => {
  if (!size) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let sizeValue = size;
  while (sizeValue >= 1024 && i < units.length - 1) {
    sizeValue /= 1024;
    i += 1;
  }
  return `${sizeValue.toFixed(1)} ${units[i]}`;
};

const formatDate = date => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
};

// Fonction pour ouvrir un fichier
const openFile = async file => {
  selectedFile.value = file;
  showFileModal.value = true;
  loadingFile.value = true;
  fileError.value = null;
  fileContent.value = null;

  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (Auth.hasAuthCookie()) {
      const authData = Auth.getAuthData();
      if (authData && authData['access-token']) {
        headers['access-token'] = authData['access-token'];
        headers['token-type'] = authData['token-type'];
        headers.client = authData.client;
        headers.expiry = authData.expiry;
        headers.uid = authData.uid;
      }
    }

    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/drive_files/${file.id}/download`,
      {
        headers,
        responseType: 'blob',
      }
    );

    const blob = response.data;
    const url = URL.createObjectURL(blob);

    fileContent.value = {
      url,
      type: file.file_type || 'other',
      contentType: response.headers['content-type'] || file.content_type,
      name: file.name,
    };
  } catch (error) {
    console.error('Erreur lors du chargement du fichier:', error);
    fileError.value = 'Impossible de charger le fichier';
  } finally {
    loadingFile.value = false;
  }
};

// Fonction pour fermer la modal
const closeFileModal = () => {
  showFileModal.value = false;
  selectedFile.value = null;
  if (fileContent.value?.url) {
    URL.revokeObjectURL(fileContent.value.url);
  }
  fileContent.value = null;
  fileError.value = null;
};

// Fonction pour télécharger un fichier
const downloadFile = async file => {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (Auth.hasAuthCookie()) {
      const authData = Auth.getAuthData();
      if (authData && authData['access-token']) {
        headers['access-token'] = authData['access-token'];
        headers['token-type'] = authData['token-type'];
        headers.client = authData.client;
        headers.expiry = authData.expiry;
        headers.uid = authData.uid;
      }
    }

    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/drive_files/${file.id}/download`,
      {
        headers,
        responseType: 'blob',
      }
    );

    const blob = response.data;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erreur lors du téléchargement:', error);
  }
};

// Fonction pour uploader des fichiers
const uploadFiles = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.onchange = async event => {
    const files = Array.from(event.target.files);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('drive_file[name]', file.name);
        formData.append('drive_file[folder_path]', rootFolder.value);

        const headers = {};

        if (Auth.hasAuthCookie()) {
          const authData = Auth.getAuthData();
          if (authData && authData['access-token']) {
            headers['access-token'] = authData['access-token'];
            headers['token-type'] = authData['token-type'];
            headers.client = authData.client;
            headers.expiry = authData.expiry;
            headers.uid = authData.uid;
          }
        }

        await axios.post(
          `/api/v1/accounts/${accountId.value}/drive_files`,
          formData,
          {
            headers,
          }
        );

        // Recharger les fichiers après l'upload
        window.location.reload();
      } catch (error) {
        console.error("Erreur lors de l'upload:", error);
        alert(
          `Erreur lors de l'upload de ${file.name}: ${error.response?.data?.error || error.message}`
        );
      }
    }
  };
  input.click();
};

// Fonction pour déterminer si un fichier peut être prévisualisé
const canPreview = file => {
  const previewableTypes = ['image', 'pdf', 'text'];
  return previewableTypes.includes(file.file_type);
};

// Calcul des statistiques de stockage
const totalSizeBytes = computed(() => {
  return items.value.reduce((sum, file) => sum + (file.file_size || 0), 0);
});

const totalSizeGB = computed(() => {
  return totalSizeBytes.value / (1024 * 1024 * 1024);
});

const storageLimitGB = computed(() => {
  // Limite par défaut de 100 GB, peut être configurée via widget.props
  return props.widget.props?.storageLimit || 100;
});

const storageUsagePercent = computed(() => {
  if (storageLimitGB.value === 0) return 0;
  return Math.min((totalSizeGB.value / storageLimitGB.value) * 100, 100);
});

// Calcul des catégories par type de fichier
const getFileCategory = file => {
  const fileType = file.file_type?.toLowerCase() || '';
  const ext = file.name?.split('.').pop()?.toLowerCase() || '';

  // Documents
  if (
    ['document', 'pdf', 'text'].includes(fileType) ||
    [
      'pdf',
      'doc',
      'docx',
      'txt',
      'rtf',
      'odt',
      'xls',
      'xlsx',
      'ppt',
      'pptx',
    ].includes(ext)
  ) {
    return 'docs';
  }

  // Media
  if (
    ['image', 'video', 'audio'].includes(fileType) ||
    [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'svg',
      'webp',
      'mp4',
      'avi',
      'mov',
      'mp3',
      'wav',
      'ogg',
    ].includes(ext)
  ) {
    return 'media';
  }

  // Other
  return 'other';
};

const categoryStats = computed(() => {
  const stats = {
    docs: { size: 0, count: 0 },
    media: { size: 0, count: 0 },
    other: { size: 0, count: 0 },
  };

  items.value.forEach(file => {
    const category = getFileCategory(file);
    const fileSize = file.file_size || 0;
    stats[category].size += fileSize;
    stats[category].count += 1;
  });

  return {
    docs: {
      size: formatFileSize(stats.docs.size),
      count: stats.docs.count,
    },
    media: {
      size: formatFileSize(stats.media.size),
      count: stats.media.count,
    },
    other: {
      size: formatFileSize(stats.other.size),
      count: stats.other.count,
    },
  };
});

// Fichiers récents triés par date
const recentFiles = computed(() => {
  return [...items.value]
    .filter(file => file.name !== '.folder')
    .sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at || 0);
      const dateB = new Date(b.updated_at || b.created_at || 0);
      return dateB - dateA;
    })
    .slice(0, 3);
});
</script>

<template>
  <div class="flex flex-col group/drive h-full p-6">
    <!-- Debug Info (visible en développement) -->
    <div
      v-if="error"
      class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
    >
      <div class="text-sm text-red-700">
        <strong>Erreur API:</strong> {{ error }}
      </div>
    </div>

    <div
      v-if="loading"
      class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg"
    >
      <div class="text-sm text-blue-700">Chargement des fichiers...</div>
    </div>

    <div
      v-if="!loading && items.length === 0"
      class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
    >
      <div class="text-sm text-yellow-700">
        <strong>Aucun fichier trouvé</strong><br />
        Dossier configuré: {{ rootFolder }}<br />
        Données reçues: {{ !!data }}
      </div>
    </div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shadow-lg shadow-slate-200"
        >
          <span class="i-lucide-cloud text-lg text-white" />
        </div>
        <div>
          <span
            class="text-[11px] font-black text-slate-900 uppercase tracking-wider block leading-none"
            >{{ title || 'Cloud Drive' }}</span
          >
          <span
            class="text-[9px] font-bold text-slate-400 uppercase tracking-tight mt-1 block"
            >Storage System</span>
        </div>
      </div>
      <button
        class="w-8 h-8 rounded-full bg-slate-50 text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all flex items-center justify-center"
      >
        <span class="i-lucide-more-horizontal text-base !text-[#0F182A]" />
      </button>
    </div>

    <!-- Categories -->
    <div class="grid grid-cols-3 gap-3 mb-6">
      <div
        class="bg-slate-50 rounded-2xl p-3 border border-slate-100 flex flex-col items-center justify-center group/cat hover:bg-white hover:border-emerald-200 hover:shadow-md hover:shadow-emerald-500/5 transition-all cursor-pointer"
      >
        <div
          class="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-2 shadow-sm"
        >
          <span class="i-lucide-file-text text-sm !text-[#0F182A]" />
        </div>
        <span class="text-[9px] font-black text-slate-900 uppercase">Docs</span>
        <span class="text-[9px] font-bold text-emerald-600 mt-0.5">{{
          categoryStats.docs.size
        }}</span>
      </div>
      <div
        class="bg-slate-50 rounded-2xl p-3 border border-slate-100 flex flex-col items-center justify-center group/cat hover:bg-white hover:border-amber-200 hover:shadow-md hover:shadow-amber-500/5 transition-all cursor-pointer"
      >
        <div
          class="w-8 h-8 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center mb-2 shadow-sm"
        >
          <span class="i-lucide-image text-sm !text-[#0F182A]" />
        </div>
        <span class="text-[9px] font-black text-slate-900 uppercase"
          >Media</span
        >
        <span class="text-[9px] font-bold text-amber-600 mt-0.5">{{
          categoryStats.media.size
        }}</span>
      </div>
      <div
        class="bg-slate-50 rounded-2xl p-3 border border-slate-100 flex flex-col items-center justify-center group/cat hover:bg-white hover:border-blue-200 hover:shadow-md hover:shadow-blue-500/5 transition-all cursor-pointer"
      >
        <div
          class="w-8 h-8 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-2 shadow-sm"
        >
          <span class="i-lucide-layers text-sm !text-[#0F182A]" />
        </div>
        <span class="text-[9px] font-black text-slate-900 uppercase">Other</span>
        <span class="text-[9px] font-bold text-blue-600 mt-0.5">{{
          categoryStats.other.size
        }}</span>
      </div>
    </div>

    <!-- Recent Files -->
    <div class="flex flex-col">
      <div class="flex items-center justify-between mb-3 px-1">
        <span
          class="text-[10px] font-black text-slate-900 uppercase tracking-widest"
          >Recent Files</span>
        <button
          class="text-[10px] font-black text-slate-400 hover:text-slate-900 transition-colors"
        >
          View All
        </button>
      </div>

      <div
        class="overflow-y-auto custom-scrollbar space-y-2 pr-1 max-h-[200px]"
      >
        <div
          v-for="file in recentFiles"
          :key="file.id"
          class="flex items-center justify-between group/file p-2.5 rounded-2xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all cursor-pointer"
          @click="openFile(file)"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div
              class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border shadow-sm"
              :class="
                file.name.endsWith('.pdf')
                  ? 'bg-rose-50 border-rose-100'
                  : file.name.endsWith('.zip')
                    ? 'bg-amber-50 border-amber-100'
                    : 'bg-blue-50 border-blue-100'
              "
            >
              <span
                :class="getFileIcon(file)"
                class="text-base !text-[#0F182A]"
              />
            </div>
            <div class="min-w-0">
              <div class="text-[12px] font-bold text-slate-900 truncate">
                {{ file.name }}
              </div>
              <div class="text-[10px] font-medium text-slate-400">
                {{ formatFileSize(file.file_size) }}
              </div>
            </div>
          </div>
          <button
            class="w-8 h-8 rounded-full flex items-center justify-center text-slate-300 hover:text-slate-900 hover:bg-white hover:shadow-sm opacity-0 group-hover/file:opacity-100 transition-all"
            :title="'Télécharger ' + file.name"
            @click.stop="downloadFile(file)"
          >
            <span class="i-lucide-download text-sm !text-[#0F182A]" />
          </button>
        </div>
      </div>

      <!-- Bottom Action -->
      <button
        class="mt-3 w-full py-2.5 rounded-2xl bg-slate-900 text-white font-black text-[11px] uppercase tracking-widest shadow-lg shadow-slate-200 hover:bg-black hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-2"
        @click="uploadFiles"
      >
        <span class="i-lucide-upload-cloud text-sm text-white" />
        Upload Files
      </button>
    </div>

    <!-- Modal de visualisation des fichiers (Interface Ultra-Pro) -->
    <Teleport to="body">
      <div
        v-if="showFileModal"
        class="fixed inset-0 z-[99999] flex items-center justify-center p-0 sm:p-8"
      >
        <!-- Backdrop Minimaliste & Profond -->
        <div
          class="absolute inset-0 bg-[#0F172A]/95 backdrop-blur-md transition-all duration-500"
          @click="closeFileModal"
        />

        <!-- Container Modal (Style Studio) -->
        <div
          class="relative bg-white dark:bg-[#0B0F1A] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] w-full max-w-7xl h-full max-h-full sm:max-h-[92vh] flex flex-col overflow-hidden border border-white/10 sm:rounded-2xl animate-in fade-in zoom-in slide-in-from-bottom-8 duration-500 ease-out"
          @click.stop
        >
          <!-- Top Bar (Minimaliste & Flottante) -->
          <div
            class="flex items-center justify-between px-8 py-5 border-b border-slate-100 dark:border-white/5 bg-white/80 dark:bg-[#0B0F1A]/80 backdrop-blur-xl z-20"
          >
            <div class="flex items-center gap-5 min-w-0">
              <div
                class="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm border border-slate-100 dark:border-white/10"
                :class="
                  selectedFile?.name.endsWith('.pdf')
                    ? 'bg-rose-50 dark:bg-rose-500/5 text-rose-500'
                    : selectedFile?.name.endsWith('.zip')
                      ? 'bg-amber-50 dark:bg-amber-500/5 text-amber-500'
                      : 'bg-blue-50 dark:bg-blue-500/5 text-blue-500'
                "
              >
                <span :class="getFileIcon(selectedFile)" class="text-xl" />
              </div>
              <div class="min-w-0">
                <h3
                  class="text-sm font-bold text-slate-900 dark:text-white truncate tracking-tight"
                >
                  {{ selectedFile?.name }}
                </h3>
                <div class="flex items-center gap-3 mt-1">
                  <span
                    class="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]"
                  >
                    {{ selectedFile?.name.split('.').pop() }}
                  </span>
                  <div
                    class="w-1 h-1 rounded-full bg-slate-200 dark:bg-white/10"
                  />
                  <span
                    class="text-[11px] font-medium text-slate-400 dark:text-slate-500"
                  >
                    {{ formatFileSize(selectedFile?.file_size) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="flex items-center gap-4">
              <button
                class="flex items-center gap-2.5 px-5 py-2.5 bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] rounded-xl font-bold text-[11px] uppercase tracking-widest hover:opacity-90 active:scale-[0.98] transition-all"
                @click="downloadFile(selectedFile)"
              >
                <span class="i-lucide-download text-sm" />
                <span class="hidden sm:inline">Download</span>
              </button>
              <div class="w-px h-6 bg-slate-200 dark:bg-white/10 mx-1" />
              <button
                class="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all"
                @click="closeFileModal"
              >
                <span class="i-lucide-x text-xl" />
              </button>
            </div>
          </div>

          <!-- Main Content Area (Focus sur le contenu) -->
          <div
            class="flex-1 overflow-hidden relative bg-[#F8FAFC] dark:bg-[#080B14]"
          >
            <!-- Loading State -->
            <div
              v-if="loadingFile"
              class="absolute inset-0 flex items-center justify-center z-10"
            >
              <div class="flex flex-col items-center gap-6">
                <div class="relative w-12 h-12">
                  <div
                    class="absolute inset-0 border-2 border-slate-200 dark:border-white/5 rounded-full"
                  />
                  <div
                    class="absolute inset-0 border-2 border-[#0F172A] dark:border-white border-t-transparent rounded-full animate-spin"
                  />
                </div>
                <p
                  class="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] animate-pulse"
                >
                  Loading Assets
                </p>
              </div>
            </div>

            <!-- Error State -->
            <div
              v-else-if="fileError"
              class="h-full flex items-center justify-center p-12 text-center"
            >
              <div class="max-w-xs">
                <div
                  class="w-16 h-16 bg-rose-50 dark:bg-rose-500/5 text-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-rose-100 dark:border-rose-500/10"
                >
                  <span class="i-lucide-alert-circle text-3xl" />
                </div>
                <h4
                  class="text-lg font-bold text-slate-900 dark:text-white mb-2"
                >
                  Preview Unavailable
                </h4>
                <p
                  class="text-sm text-slate-500 dark:text-slate-400 mb-8 leading-relaxed"
                >
                  {{ fileError }}
                </p>
                <button
                  class="w-full py-4 bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] rounded-xl font-bold text-xs uppercase tracking-widest hover:opacity-90 transition-all"
                  @click="downloadFile(selectedFile)"
                >
                  Download File
                </button>
              </div>
            </div>

            <!-- Preview Area -->
            <div
              v-else-if="fileContent"
              class="h-full w-full flex items-center justify-center"
            >
              <!-- Image Preview (Clean Studio View) -->
              <div
                v-if="fileContent.type === 'image'"
                class="h-full w-full flex items-center justify-center p-8 sm:p-12"
              >
                <img
                  :src="fileContent.url"
                  :alt="fileContent.name"
                  class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                />
              </div>

              <!-- PDF Preview (Edge-to-Edge Reader) -->
              <div
                v-else-if="fileContent.type === 'pdf'"
                class="w-full h-full bg-[#525659]"
              >
                <iframe
                  :src="fileContent.url"
                  class="w-full h-full border-0"
                  title="PDF Preview"
                />
              </div>

              <!-- Text/Code Preview (Clean Editor) -->
              <div
                v-else-if="fileContent.type === 'text'"
                class="w-full h-full flex flex-col bg-white dark:bg-[#0D1117]"
              >
                <iframe
                  :src="fileContent.url"
                  class="w-full flex-1 border-0"
                  title="Text Preview"
                />
              </div>

              <!-- No Preview (Minimalist Fallback) -->
              <div v-else class="text-center p-12">
                <div
                  class="w-20 h-20 bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-600 rounded-2xl flex items-center justify-center mx-auto mb-8 border border-slate-200 dark:border-white/5"
                >
                  <span class="i-lucide-file-search text-4xl" />
                </div>
                <h4
                  class="text-xl font-bold text-slate-900 dark:text-white mb-3"
                >
                  No Preview
                </h4>
                <p
                  class="text-sm text-slate-500 dark:text-slate-400 mb-10 max-w-xs mx-auto leading-relaxed"
                >
                  This file type cannot be previewed. Please download it to view
                  the content.
                </p>
                <button
                  class="px-12 py-4 bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] rounded-xl font-bold text-xs uppercase tracking-widest hover:opacity-90 active:scale-[0.98] transition-all"
                  @click="downloadFile(selectedFile)"
                >
                  Download
                </button>
              </div>
            </div>
          </div>

          <!-- Footer Bar (Ultra-Minimal) -->
          <div
            class="px-8 py-4 bg-white dark:bg-[#0B0F1A] border-t border-slate-100 dark:border-white/5 flex items-center justify-between"
          >
            <div
              class="flex items-center gap-6 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]"
            >
              <div class="flex items-center gap-2">
                <div
                  class="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                />
                Encrypted
              </div>
              <div class="flex items-center gap-2">
                <span class="i-lucide-clock text-xs" />
                Modified {{ formatDate(selectedFile?.updated_at) }}
              </div>
            </div>
            <div
              class="text-[10px] font-black text-slate-300 dark:text-white/10 uppercase tracking-[0.2em]"
            >
              Secure Asset Viewer
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
